import { Component, effect, ElementRef, input, output, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { startWith, map, } from 'rxjs/operators';
import { get, groupBy, differenceBy, concat } from 'lodash';
import { SearchComponent } from '../search/search.component';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { Node } from '@xanadu/model';
import { PickerValidator } from '../validator/picker-validator';
import { InputConstant } from '../input.constant';

@Component({
    selector: 'picker',
    template: '',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export abstract class PickerComponent<T extends Node> extends SearchComponent {
    node = input<Node>();
    groupBy = input<string>();
    lockModel = input<any[]>();

    pick = output<T>();
    delete = output<T>();
    change = output<T[]>();

    readonly searchRef = viewChild<ElementRef<HTMLInputElement>>('search');

    pickerArray?: FormArray<any>;
    arrayLock?: FormArray<any>;

    nodes: T[] = [];
    locks: T[] = [];

    protected abstract filter(node: T): boolean;

    constructor() {
        super();

        effect(() => {
            const formGroup = this.formGroup();
            const formControl = this.formControl();
            const meta = this.meta();

            if (formGroup) {
                formGroup.addControl(`${InputConstant.ignorePrefix}${meta.id}`, formControl);
            }

            this.pickerArray = new FormArray<any>([]);
            this.arrayLock = new FormArray<any>([]);

            formGroup?.addControl(meta.id, this.pickerArray);
            formControl?.setValidators([PickerValidator(this.pickerArray, meta)]);
        });

        effect(() => {
            const formControl = this.formControl();
            const items = this.items();
            const node = this.node();
            const by = this.groupBy();

            if (formControl && items?.length) {
                this.filtered = formControl.valueChanges
                    .pipe(
                        startWith(''),
                        map(value => typeof value === 'string' ? value : (value as any)?.name),
                        map(name => name ? this.filterItems(name) : items.slice()),
                        map(nodes => this.filterBy(nodes)),
                        map(nodes => differenceBy(nodes, concat(this.nodes, this.locks, node), '_id')),
                        map(nodes => groupBy(nodes, node => by ? get(node, by) : node.parent?.name)),
                        map(object => Object.keys(object).map(key => ({ key: key, items: object[key] })))
                    );
            }
        });

        effect(() => {
            const ids = this.model();
            const items = this.items();
            const meta = this.meta();

            if (meta && items?.length && this.pickerArray) {
                this.nodes = ids ? ids
                    .map((id: any) => items.find(item => item._id === id))
                    .filter((node: T | undefined)=> !!node) : [];

                this.setItems(this.pickerArray, this.nodes);

                if (this.nodes) {
                    meta.readonly = meta.max > 0 && this.nodes.length >= meta.max;
                }
            }
        });

        effect(() => {
            const lockIds = this.lockModel();
            const items = this.items();

            if (Array.isArray(lockIds) && lockIds.length && this.arrayLock && items?.length) {
                this.locks = lockIds
                    .map(id => items.find(item => item._id === id))
                    .filter((node): node is T => node !== undefined);

                this.setItems(this.arrayLock, this.locks);
            }
        });
    }

    protected setDefault(): void {
    }

    protected onDelete(index: any): void {
        if (this.nodes.length <= index) {
            return;
        }

        const deleted = this.nodes.splice(index, 1);
        if (deleted?.length) {
            this.delete.emit(deleted[0]);
            this.change.emit(this.nodes);
        }
        this.refresh();
        this.formGroup()?.markAsDirty();
        this.formControl()?.updateValueAndValidity();
    }

    onPick($event: MatAutocompleteSelectedEvent): void {
        super.onPick($event);

        const content = $event.option.value;
        const meta = this.meta();
        if (!meta.max || meta.max.length <= 0 || this.nodes.length < meta.max) {
            if (content) {
                this.pick.emit(content);
            }
            this.nodes.push(content);
            this.change.emit(this.nodes);

            this.refresh();
            this.formGroup()?.markAsDirty();
        }
    }

    private refresh(): void {
        const meta = this.meta();
        this.formControl()?.setValue('');
        if (this.pickerArray) {
            this.setItems(this.pickerArray, this.nodes);
        }
        this.searchRef()?.nativeElement?.blur();
        meta.readonly = meta.max > 0 && (this.nodes?.length ?? 0)>= meta.max;
    }

    private setItems(
        array: FormArray<FormControl<string>>,
        nodes: T[]
    ): void {
        array.clear({ emitEvent: false });

        for (const node of nodes) {
            if (node?._id) {
                const control = new FormControl<string>(node.name!, {
                    nonNullable: true
                });

                array.push(control, { emitEvent: false });

                setTimeout(() => {
                    control.setValue(node._id!, {
                        emitEvent: false,
                        emitModelToViewChange: false
                    });
                });
            }
        }
    }

    private filterBy(nodes: T[]): T[] {
        if (nodes && nodes.length > 0) {
            const result = nodes.filter(node => this.filter(node));
            return result;
        }
        return nodes;
    }
}
